/**
 * Engineering formatting. The rules come from DESIGN.md §3: values are monospaced tabular figures, units
 * are never abbreviated away (µm pk-pk is never just "µm"), and a number without its provenance is
 * incomplete. Everything here is pure so the same functions can be unit-tested and reused in the
 * how-it-works document generator.
 */

/** Band → presentation. The ramp mirrors API 670 Alert/Danger; no invented levels. */
export type Band = 'STABLE' | 'WATCH' | 'WARNING' | 'ALERT' | 'DANGER' | 'NOVEL' | 'UNVERIFIED' | 'NONE' | (string & Record<never, never>);

export interface BandStyle {
  label: string;
  /** Tag modifier class from styles.css; also used for the left rule on panels. */
  tag: string;
  panel: string;
  rank: number;
  colour: string;
}

const BANDS: Record<string, BandStyle> = {
  STABLE: { label: 'Stable', tag: 'tag--ok', panel: '', rank: 0, colour: 'var(--ok)' },
  NONE: { label: 'No findings', tag: 'tag--ok', panel: '', rank: 0, colour: 'var(--ok)' },
  NOVEL: { label: 'Novel regime', tag: 'tag--info', panel: '', rank: 1, colour: 'var(--info)' },
  WATCH: { label: 'Watch', tag: 'tag--watch', panel: 'panel--state-watch', rank: 2, colour: 'var(--watch)' },
  WARNING: { label: 'Warning', tag: 'tag--watch', panel: 'panel--state-watch', rank: 3, colour: 'var(--watch)' },
  ALERT: { label: 'Alert', tag: 'tag--alert', panel: 'panel--state-alert', rank: 4, colour: 'var(--alert)' },
  DANGER: { label: 'Danger', tag: 'tag--danger', panel: 'panel--state-danger', rank: 5, colour: 'var(--danger)' },
  UNVERIFIED: { label: 'Unverified', tag: 'tag--muted', panel: '', rank: -1, colour: 'var(--unavailable)' },
};

export const bandStyle = (band: string | null | undefined): BandStyle => BANDS[String(band ?? 'UNVERIFIED').toUpperCase()] ?? BANDS.UNVERIFIED!;

export const bandRankOf = (band: string | null | undefined): number => bandStyle(band).rank;

const numberFormatters = new Map<string, Intl.NumberFormat>();
const formatter = (digits: number): Intl.NumberFormat => {
  const key = String(digits);
  const existing = numberFormatters.get(key);
  if (existing) return existing;
  const created = new Intl.NumberFormat('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  numberFormatters.set(key, created);
  return created;
};

/** Fixed-precision engineering value. `null`/NaN render as an em dash rather than 0. */
export const value = (input: number | string | null | undefined, digits = 2): string => {
  const numeric = typeof input === 'string' ? Number(input) : input;
  if (numeric === null || numeric === undefined || !Number.isFinite(numeric)) return '—';
  return formatter(digits).format(numeric);
};

/** Value plus unit, kept as one call site so no component invents its own spacing. */
export const withUnit = (input: number | string | null | undefined, unit: string, digits = 2): string => `${value(input, digits)} ${unit}`;

export const percent = (input: number | null | undefined, digits = 0): string => (input === null || input === undefined || !Number.isFinite(input) ? '—' : `${value(input * 100, digits)} %`);

export const integer = (input: number | string | null | undefined): string => {
  const numeric = typeof input === 'string' ? Number(input) : input;
  if (numeric === null || numeric === undefined || !Number.isFinite(numeric)) return '—';
  return new Intl.NumberFormat('en-GB').format(Math.round(numeric));
};

export const money = (cents: number | null | undefined, currency = 'EUR'): string => {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency, minimumFractionDigits: 2 }).format(cents / 100);
};

const TIME_FORMAT = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });

export const dateTime = (input: string | number | Date | null | undefined): string => {
  const date = toDate(input);
  return date ? TIME_FORMAT.format(date) : '—';
};

export const timeOnly = (input: string | number | Date | null | undefined): string => {
  const date = toDate(input);
  if (!date) return '—';
  return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(date);
};

export const dayOnly = (input: string | number | Date | null | undefined): string => {
  const date = toDate(input);
  if (!date) return '—';
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(date);
};

/** "4 min ago" / "in 6 weeks" — used for staleness banners where an absolute time would be read slower. */
export const relative = (input: string | number | Date | null | undefined, now: number = Date.now()): string => {
  const date = toDate(input);
  if (!date) return '—';
  const deltaMs = date.getTime() - now;
  const abs = Math.abs(deltaMs);
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 31_557_600_000],
    ['month', 2_629_800_000],
    ['week', 604_800_000],
    ['day', 86_400_000],
    ['hour', 3_600_000],
    ['minute', 60_000],
    ['second', 1000],
  ];
  for (const [unit, ms] of units) {
    if (abs >= ms || unit === 'second') {
      const rtf = new Intl.RelativeTimeFormat('en-GB', { numeric: 'auto', style: 'narrow' });
      return rtf.format(Math.round(deltaMs / ms), unit);
    }
  }
  return '—';
};

/** Days with the granularity a reliability engineer actually quotes at. */
export const days = (input: number | null | undefined): string => {
  if (input === null || input === undefined || !Number.isFinite(input)) return '—';
  if (input < 1) return `${value(input * 24, 0)} h`;
  if (input < 14) return `${value(input, 1)} d`;
  if (input < 90) return `${value(input, 0)} d`;
  return `${value(input / 30.44, 1)} months`;
};

export const toDate = (input: string | number | Date | null | undefined): Date | null => {
  if (input === null || input === undefined) return null;
  const date = input instanceof Date ? input : new Date(input);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const staleMinutes = (input: string | number | Date | null | undefined, now: number = Date.now()): number | null => {
  const date = toDate(input);
  return date === null ? null : (now - date.getTime()) / 60_000;
};

/**
 * Provenance tokens (DESIGN.md §1 rule 3). A derived number is meaningless without the sample count,
 * regime and rate it came from, so the component that shows one takes these as one argument.
 */
export interface Provenance {
  /** Which table or view the numbers were read from, e.g. `telemetry_scalar`. */
  source?: string | null;
  /** The span and mode the read covers, e.g. `7 d · raw buckets`. */
  window?: string | null;
  /** Roll-up of the data quality, e.g. `GOOD` or `PARTIAL`. */
  quality?: string | null;
  samples?: number | null;
  regime?: string | null;
  rateHz?: number | null;
  standard?: string | null;
  model?: string | null;
  engine?: string | null;
  asOf?: string | null;
  provenance?: string | null;
}

export const provenanceTokens = (input: Provenance): string[] => {
  const tokens: string[] = [];
  if (input.source) tokens.push(`from ${input.source}`);
  if (input.window) tokens.push(input.window);
  if (input.quality && input.quality !== 'GOOD') tokens.push(`quality ${input.quality.toLowerCase()}`);
  if (input.rateHz) tokens.push(`${value(input.rateHz, input.rateHz >= 100 ? 0 : 2)} Hz`);
  if (input.samples !== undefined && input.samples !== null) tokens.push(`n=${integer(input.samples)}`);
  if (input.regime) tokens.push(String(input.regime));
  if (input.standard) tokens.push(input.standard);
  if (input.engine) tokens.push(input.engine);
  if (input.model) tokens.push(input.model);
  if (input.provenance && input.provenance !== 'HISTORIAN') tokens.push(input.provenance);
  if (input.asOf) tokens.push(`as of ${dateTime(input.asOf)}`);
  return tokens;
};

export const initials = (name: string | null | undefined, email: string | null | undefined): string => {
  const source = (name ?? email ?? '').trim();
  if (!source) return '?';
  const letters = source.split(/[\s.@_-]+/).filter(Boolean).slice(0, 2).map((part) => part.charAt(0).toUpperCase());
  return letters.join('') || '?';
};

/** Truncated-mean style summary used across the fleet wall; keeps the "worst, not average" rule honest. */
export const worstBand = (bands: Array<string | null | undefined>): Band => {
  let best: Band = 'NONE';
  let rank = -1;
  for (const band of bands) {
    const style = bandStyle(band);
    if (style.rank > rank) {
      rank = style.rank;
      best = (band ?? 'NONE') as Band;
    }
  }
  return best;
};

export const pluralise = (count: number, singular: string, plural = `${singular}s`): string => `${integer(count)} ${count === 1 ? singular : plural}`;
